import React, { useCallback, useContext, useEffect, useState } from 'react';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import { PRESCRIPTION_STATUS } from 'utils/prescription';
import moment from 'moment';
import { DashboardContext } from '../context';
import TabWrapper from '../TabWrapper';
import Header from './Header';
import { overviewService } from './services';
import styles from './styles.module.scss';
import WidgetLessons from './WidgetLessons';
import WidgetRewards from './WidgetRewards';
import WidgetSubstanceUse from './WidgetSubstanceUse';
import WidgetUrineDrugScreen from './WidgetUrineDrugScreen';

const Overview = () => {
  const subtitleLessons = 'Lessons Completed';
  const subtitleDrugScreens = 'Urine Drug Screens';
  const [subtitleSubstanceUses, setSubtitleSubstanceUses] =
    useState<string>('Days Substance Use');
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const {
    overview,
    setOverview,
    patientId,
    selectedAccessRight,
    treatmentSummary,
  } = useContext(DashboardContext);
  const { timezone } = selectedAccessRight!;
  const title = treatmentSummary!.lastClinicVisit
    ? 'Progress Since Last Clinic Visit'
    : 'Progress Since Last 30 Days';
  const getOverview = useCallback(async () => {
    try {
      const fetchedOverview = await overviewService.getPatientOverview(
        patientId!,
        selectedAccessRight!.id,
      );

      setOverview({
        ...fetchedOverview,
      });

      if (fetchedOverview.substanceUseDaysCount === 1) {
        setSubtitleSubstanceUses('Day Substance Use');
      }
    } catch (e) {
      ErrorInterceptor.error(e);
    }
  }, [patientId, selectedAccessRight, setOverview]);

  useEffect(() => {
    if (!overview) {
      getOverview();
    } else {
      setIsLoaded(true);
    }
  }, [getOverview, overview]);

  if (isLoaded && overview) {
    return (
      <div id="overview-tab-id" data-testid="overview-loaded">
        <TabWrapper>
          <Header
            title={title}
            period={`
            ${moment(overview.startDate).tz(timezone).format('l')} -
            ${
              moment(overview.endDate)
                .tz(timezone)
                .isSame(moment().tz(timezone), 'day')
                ? 'Today'
                : moment(overview.endDate).tz(timezone).format('l')
            }
          `}
          />
          <div className={styles['wrapper-widgets']}>
            <WidgetLessons
              total={overview.lessonCompletionCount}
              subtitle={subtitleLessons}
              completedLessons={overview.lessonsCompleted}
              incompletedLessons={overview.lessonsToDo}
              isProcessing={false}
            />
            <WidgetRewards
              total={overview.sumEarnedRewards}
              rewards={overview.earnedRewards}
              isProcessing={false}
              isInAppCm={overview.inAppCm || false}
            />
            <WidgetUrineDrugScreen
              subtitle={subtitleDrugScreens}
              urineDrugScreens={overview.urineDrugScreens}
              hasActivePrescription={
                selectedAccessRight!.status === PRESCRIPTION_STATUS.STARTED
              }
            />
          </div>
          <WidgetSubstanceUse
            subtitle={subtitleSubstanceUses}
            total={overview.substanceUseDaysCount}
            isEmptySubstanceUse={!overview.dailySummaries.length}
            showBupUseLabels={overview.showBupUse}
            dailySummaries={overview.dailySummaries}
            isProcessing={false}
            timezone={timezone}
          />
        </TabWrapper>
      </div>
    );
  }

  return <div data-testid="overview-loading" />;
};

export default Overview;
